import { useAuthentication } from '@app/src/context/AuthenticationContext'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { isAdminRole } from '@app/src/utils/rolePermissions'
import type { AnswerAcceptanceLevel } from '@app/src/wf-constants'
import { InfoOutlined, LinkRounded } from '@mui/icons-material'
import { Box, Divider, keyframes, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import AcceptanceValueChip from './AcceptanceValueChip'
import { acceptanceCriteria, defaultAnswerAcceptanceLevel, isAcceptedAt, valueMessageIds } from './answerAcceptance'
import AnswerAcceptanceSlider from './AnswerAcceptanceSlider'

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

const chipStaggerMs = 40

const AnswerAcceptanceSettings: React.FC = () => {
  const { formatMessage } = useIntl()
  const { role } = useAuthentication().scope
  const { settings, updateOrganizationSettings } = useOrganizationSettings()
  const [previewLevel, setPreviewLevel] = useState<AnswerAcceptanceLevel>()
  const requestedLevel = useRef<AnswerAcceptanceLevel>()
  const pendingSave = useRef(Promise.resolve())
  const savesInFlight = useRef(0)

  const savedLevel = settings?.aiAnswerAcceptanceLevel ?? defaultAnswerAcceptanceLevel
  const level = previewLevel ?? savedLevel
  const isAdmin = isAdminRole(role)
  const criteria = useMemo(() => acceptanceCriteria(new Date().getFullYear()), [])

  // The saved level only updates after the save's refetch, so a quick change back would compare
  // against a stale value. Comparing against the last requested level and saving one at a time
  // keeps the last level picked as the one that is stored.
  const saveLevel = (newLevel: AnswerAcceptanceLevel) => {
    setPreviewLevel(newLevel)
    if (newLevel === (requestedLevel.current ?? savedLevel)) return
    requestedLevel.current = newLevel
    savesInFlight.current += 1
    const save: Promise<void> = pendingSave.current
      .then(() => updateOrganizationSettings({ aiAnswerAcceptanceLevel: newLevel }))
      .catch(() => {
        if (pendingSave.current !== save) return
        requestedLevel.current = undefined
        setPreviewLevel(undefined)
      })
      .finally(() => {
        savesInFlight.current -= 1
      })
    pendingSave.current = save
  }

  // Once nothing is saving, the server's level is the truth again, also when another admin
  // changes it later.
  useEffect(() => {
    if (savesInFlight.current > 0) return
    requestedLevel.current = undefined
    setPreviewLevel(undefined)
  }, [savedLevel])

  // An API without the setting cannot save it, so the card stays hidden until it does.
  if (!settings?.aiAnswerAcceptanceLevel) return null

  return (
    <Stack bgcolor="background.paper" borderRadius={2} p={4} spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1">{formatMessage({ id: 'aiScreening.acceptance.title' })}</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'aiScreening.acceptance.description' })}
        </Typography>
      </Stack>

      <AnswerAcceptanceSlider level={level} disabled={!isAdmin} onPreview={setPreviewLevel} onCommit={saveLevel} />

      <Box
        key={level}
        bgcolor="grey.100"
        borderRadius={2}
        p={2.5}
        sx={{
          animation: `${fadeSlideIn} 300ms ease-out`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          {`${formatMessage({ id: `aiScreening.acceptance.level.${level}` })} (${formatMessage({
            id: `aiScreening.acceptance.levelSubtitle.${level}`,
          })})`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: `aiScreening.acceptance.levelDescription.${level}` })}
        </Typography>
      </Box>

      <Stack divider={<Divider />}>
        {criteria.map(criterion => (
          <Stack
            key={criterion.key}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            gap={{ xs: 1.5, md: 0 }}
            py={2}
          >
            <Stack width={{ xs: '100%', md: '50%' }} pr={{ md: 3 }}>
              <Typography variant="body1">
                {formatMessage({ id: `aiScreening.acceptance.criteria.${criterion.key}` })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatMessage({ id: `aiScreening.acceptance.criteria.${criterion.key}Description` })}
              </Typography>
            </Stack>
            <Stack width={{ xs: '100%', md: '50%' }} direction="row" flexWrap="wrap" gap={1}>
              {criterion.values.map((value, index) => {
                const messageIds = valueMessageIds(criterion, value)
                return (
                  <AcceptanceValueChip
                    key={value.key}
                    label={formatMessage({ id: messageIds.label }, criterion.messageValues)}
                    tooltip={formatMessage({ id: messageIds.description }, criterion.messageValues)}
                    accepted={isAcceptedAt(value, level)}
                    delayMs={index * chipStaggerMs}
                  />
                )
              })}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
          <LinkRounded fontSize="small" />
          <Typography variant="body2">{formatMessage({ id: 'aiScreening.acceptance.sourceLinkRequired' })}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-start" color="text.secondary">
          <InfoOutlined fontSize="small" />
          <Typography variant="body2">
            {formatMessage({ id: 'aiScreening.acceptance.appliesToNewScreenings' })}
            {!isAdmin && ` ${formatMessage({ id: 'aiScreening.acceptance.adminOnly' })}`}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default AnswerAcceptanceSettings
